// CB:BEGIN milestone-dispute-modal
import React, { useState } from 'react';
import { useApi } from '../lib/useApi';
import { useReadOnlySupportSession } from '../context/ImpersonationContext';

interface MilestoneDisputeModalProps {
  projectId: string;
  milestoneKey: string;
  milestoneName: string;
  projectAddress?: string;
  onClose: () => void;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
};

const modalStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  padding: '28px 28px 24px',
  maxWidth: 520,
  width: '100%',
  boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
  position: 'relative',
};

const titleStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 700,
  color: '#111827',
  margin: '0 0 4px 0',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#6b7280',
  margin: '0 0 20px 0',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 6,
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  fontSize: 14,
  border: '1px solid #d1d5db',
  borderRadius: 8,
  resize: 'vertical',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  minHeight: 90,
};

const hintStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#9ca3af',
  marginTop: 4,
};

const errorStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#dc2626',
  background: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: 8,
  padding: '10px 14px',
  marginBottom: 16,
};

const successStyle: React.CSSProperties = {
  fontSize: 14,
  color: '#065f46',
  background: '#ecfdf5',
  border: '1px solid #6ee7b7',
  borderRadius: 8,
  padding: '14px 16px',
  marginBottom: 16,
  lineHeight: 1.6,
};

const buttonRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  justifyContent: 'flex-end',
  marginTop: 20,
};

export default function MilestoneDisputeModal({
  projectId,
  milestoneKey,
  milestoneName,
  projectAddress,
  onClose,
}: MilestoneDisputeModalProps) {
  const { post } = useApi();
  const { isReadOnly, readOnlyReason } = useReadOnlySupportSession();
  const [description, setDescription] = useState('');
  const [additionalDetails, setAdditionalDetails] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const MIN_LEN = 10;
  const fullDescription = additionalDetails.trim()
    ? `${description.trim()}\n\nAdditional details: ${additionalDetails.trim()}`
    : description.trim();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // CB:BEGIN impersonation-readonly-block-dispute
    if (isReadOnly) {
      setError(readOnlyReason ?? 'Support view — dispute submission is disabled during an admin session.');
      return;
    }
    // CB:END impersonation-readonly-block-dispute
    if (description.trim().length < MIN_LEN) {
      setError(`Please describe the issue (at least ${MIN_LEN} characters).`);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await post(`/api/projects/${projectId}/disputes`, {
        category: 'QUALITY_ISSUE',
        description: fullDescription,
        milestoneKey,
        evidenceUrls: [],
      });
      setSubmitted(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to submit dispute. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle} role="dialog" aria-modal="true" aria-labelledby="dispute-modal-title">
        <h2 id="dispute-modal-title" style={titleStyle}>Dispute Milestone</h2>
        <p style={subtitleStyle}>
          {milestoneName}{projectAddress ? ` — ${projectAddress}` : ''}
        </p>

        {submitted ? (
          <>
            <div style={successStyle}>
              <strong>Report received.</strong> We've received your report. Our team will review it and follow up as needed.
              Escrow payments for this project are paused pending resolution.
            </div>
            <div style={buttonRowStyle}>
              <button
                type="button"
                className="btn btn-primary"
                style={{ fontSize: 14, padding: '8px 20px' }}
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && <div style={errorStyle}>{error}</div>}

            <div style={{ marginBottom: 16 }}>
              <label htmlFor="dispute-description" style={labelStyle}>
                How did the contractor not complete this milestone? <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <textarea
                id="dispute-description"
                style={textareaStyle}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the issue with the milestone submission..."
                disabled={loading}
                required
              />
              <p style={hintStyle}>{description.trim().length} characters (minimum {MIN_LEN})</p>
            </div>

            <div style={{ marginBottom: 4 }}>
              <label htmlFor="dispute-additional" style={labelStyle}>
                Additional details / evidence notes <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
              </label>
              <textarea
                id="dispute-additional"
                style={{ ...textareaStyle, minHeight: 70 }}
                value={additionalDetails}
                onChange={(e) => setAdditionalDetails(e.target.value)}
                placeholder="Any additional context, dates, or links to evidence..."
                disabled={loading}
              />
            </div>

            <p style={{ fontSize: 12, color: '#6b7280', margin: '12px 0 0' }}>
              Opening a dispute will pause all escrow payments until the issue is resolved. Our support team will be notified.
            </p>

            <div style={buttonRowStyle}>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ fontSize: 14, padding: '8px 20px' }}
                disabled={loading}
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                style={{ fontSize: 14, padding: '8px 20px', background: '#dc2626', borderColor: '#dc2626' }}
                disabled={loading || description.trim().length < MIN_LEN}
              >
                {loading ? 'Submitting…' : 'Submit Dispute'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
// CB:END milestone-dispute-modal
